import * as panelModel from './models/panel';
import addTargetEventListener from '../events/addTargetEventListener';
import * as eventsModel from './models/events';
import leadFormPanelLoadTracking from '../tracking/leadform_panel_load_tracking';
import * as validationModel from './models/validation';
import { selectors } from './elements/constants';
import * as stepModel from './models/step';
import { applyInputValidation, removeInputValidation } from './validation/applyValidation';
import { trackFormWizardEvent } from '../tracking/track_form_wizard_event';
import leadformPanelTracking from '../tracking/leadform_panel_tracking';
import dispatchCustomEvent from '../events/dispatchCustomEvent';

/**
 * Handles `formWizard.showStep` events called on flex_panels. In this case, it simply shows
 * the panel, which performs the necessary animation to slide between panels.
 * @param {Event} event - The current event being handled
 */
async function handleShowStep(event) {
  return Promise.all([
    panelModel.interpolatePanelText(event.target),
    leadFormPanelLoadTracking(event.target),
    panelModel.showPanel(event.target, event.detail)
  ]).then(() => {
    panelModel.autofocusInput(event.target);
  });
}

/**
 * Handles `formWizard.hideStep` events called on flex_panels.
 * @param {Event} event - The current event being handled
 */
function handleHideStep(event) {
  panelModel.hideLoadingAnimation(event.target);
  return panelModel.hidePanel(event.target, event.detail);
}

/**
 * Removes any custom error message from the panel.
 * @param {Event} event - The current event being handled
 */
function hideCustomError(event) {
  const panel = stepModel.getStep(event.target);
  const button = panel && panel.querySelector(selectors.nextButton);
  if(button) { removeInputValidation(button); }
}

/**
 * Handles `formWizard.completeStep` events called inside flex_panels, typically for tracking.
 * @param {Event} event - The current event being handled
 */
export const handleCompleteStep = event => {
  const stepId = stepModel.getStepId(event.target);
  if (!stepId) return;
  const eventAction = `formWizard.${stepId}.complete`;
  leadformPanelTracking(event.target);
  trackFormWizardEvent(event.target, {
    category: 'form_wizard_panel',
    action: eventAction
  });
};

/**
 * Handles `formWizard.navigate` events called inside flex_panels, for client-side validation.
 * @param {Event} event - The current event being handled
 */
export const handleNavigate = event => {
  if (event.defaultPrevented) return;
  hideCustomError(event);

  if(event.detail.direction !== 'backwards') {
    const panel = stepModel.getStep(event.target);
    if (!validationModel.validatePanel(panel)) {
      panelModel.autofocusInput(panel, true);
      event.preventDefault();
    } else {
      panelModel.showLoadingAnimation(panel);
      /** if we're adding checkmark && checkmark is not shown*/
      handleCheckmarks(panel, event);
    }
  }
};

/**
 * Handles case where we are displaying checkmarks next to inputs via Dato
 * @param {Element} el - The current panel
 * @param {Event} event - The current event being handled
 */
export function handleCheckmarks(panel, event) {
  if(needsCheckmarksShown(panel)){
    event.preventDefault();
    panel.classList.add('showCheckmark');

    setTimeout(() => {
      /** rethrow event after a delay */
      dispatchCustomEvent(event.target, event.type, {...event.detail});
    }, 350);
  }
}

/**
 * Handles `formWizard.customError` events called on a panel, typically after submission failure.
 * @param {Event} event - The current event being handled
 */
export function handleCustomError(event) {
  const panel = stepModel.getStep(event.target);
  const button = panel.querySelector(selectors.nextButton);
  if(button) {
    panelModel.hideLoadingAnimation(panel);
    applyInputValidation(button, event.detail.message);
  }
}

/**
 * Returns true if the field in the panel has the confirm-checkmark class
 * and if the showCheckmark class has not been added to the panel yet
 * @param {Element} el - The current panel
 */
 export function needsCheckmarksShown(panel) {
  if(panel.querySelector('.form-field--confirm-checkmark') && !panel.classList.contains('showCheckmark')){
    return true;
  }
}

addTargetEventListener(eventsModel.events.showStep, selectors.flexPanel, handleShowStep);
addTargetEventListener(eventsModel.events.hideStep, selectors.flexPanel, handleHideStep);
addTargetEventListener(eventsModel.events.completeStep, selectors.flexPanel, handleCompleteStep);
addTargetEventListener(eventsModel.events.navigate, selectors.flexPanel, handleNavigate);
addTargetEventListener(eventsModel.events.customError, selectors.flexPanel, handleCustomError);
